import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { ROLE_INFO, SCORE_LABELS, TEAM_NAMES, teamOf, type PlayerView, type ScoreReason } from '@mafia/core';
import { useGame } from '@/store/game';
import { Button, Card } from '@/components/ui';

export function GameOver({ view }: { view: PlayerView }) {
  const winner = view.winner!;
  const me = view.me;
  const iWon = me && winner !== 'DRAW' && teamOf(me.role) === winner && !view.players.find((p) => p.seat === me.seat)?.left;
  const closeGame = useGame((s) => s.closeGame);
  const scoreOf = (seat: number) => view.scores?.find((s) => s.seat === seat);
  const myScore = me ? scoreOf(me.seat) : undefined;

  return (
    <div className="mx-auto max-w-3xl animate-fade-in px-4 py-8">
      <div className="mb-6 text-center">
        <div className="text-6xl">{winner === 'MAFIA' ? '🕶️' : winner === 'TOWN' ? '🏙️' : winner === 'MANIAC' ? '🔪' : '🤝'}</div>
        <h1 className="mt-3 font-display text-4xl">{winner === 'DRAW' ? 'Ничья' : `Победа: ${TEAM_NAMES[winner]}`}</h1>
        {me && (
          <p className={clsx('mt-2 text-lg', iWon ? 'text-emerald-300' : 'text-zinc-400')}>
            {iWon ? 'Вы победили!' : 'Вы проиграли'} · вы играли за роль «{ROLE_INFO[me.role].name}»
          </p>
        )}
        {myScore && (
          <p className="mt-1 text-lg">
            Ваши баллы: <b className={myScore.total < 0 ? 'text-blood-400' : 'text-gold-300'}>{myScore.total}</b>
          </p>
        )}
        <p className="mt-1 text-sm text-zinc-500">Игра длилась {view.day} {view.day === 1 ? 'день' : view.day < 5 ? 'дня' : 'дней'}</p>
      </div>

      <Card className="overflow-hidden">
        <table className="table-base">
          <thead>
            <tr>
              <th>№</th>
              <th>Игрок</th>
              <th>Роль</th>
              <th className="text-right">Баллы</th>
              <th className="text-right">Итог</th>
            </tr>
          </thead>
          <tbody>
            {view.players.map((p) => {
              const won = winner !== 'DRAW' && p.role && teamOf(p.role) === winner && !p.left;
              return (
                <tr key={p.seat} className={clsx(p.seat === me?.seat && 'bg-gold-400/5')}>
                  <td className="font-mono text-zinc-500">{p.seat}</td>
                  <td className="font-medium">{p.nickname}</td>
                  <td>{p.role && `${ROLE_INFO[p.role].emoji} ${ROLE_INFO[p.role].name}`}</td>
                  <td
                    className="text-right font-semibold tabular-nums text-gold-300"
                    title={scoreOf(p.seat)
                      ?.entries.map((e) => `${SCORE_LABELS[e.reason as ScoreReason] ?? e.reason}: ${e.points > 0 ? '+' : ''}${e.points}`)
                      .join('\n')}
                  >
                    {scoreOf(p.seat)?.total ?? 0}
                  </td>
                  <td className="text-right text-xs">
                    <span className={won ? 'text-emerald-300' : 'text-zinc-500'}>{won ? 'победа' : 'поражение'}</span>
                    <span className="text-zinc-600"> · {p.alive ? 'жив' : p.deathReason === 'VOTE' ? 'исключён' : p.deathReason === 'LEFT' ? 'вышел' : 'убит'}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-center">
        <Button size="lg" variant="gold" onClick={closeGame}>
          Вернуться в лобби
        </Button>
        <Link to={`/games/${view.gameId}`}>
          <Button size="lg" variant="secondary" className="w-full">
            История партии
          </Button>
        </Link>
      </div>
    </div>
  );
}
